import styled from '@emotion/styled';
import {
  Box,
  Fade,
  ListItemText,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import type { AxiosResponse } from 'axios';
import { concat } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import AnimatedFooter from '../../components/dialogs/AnimatedFooter';
import { Snackbar } from '../../components/Snackbar';
import { StandardLibraryTable } from '../../components/table/StandardLibraryTable';
import { TextField } from '../../components/TextField';
import { useStandardsLibrary } from '../../contexts/StandardsLibraryContext';
import { useAppMutation } from '../../hooks/useAppMutation';
import { useAppQuery } from '../../hooks/useAppQuery';
import {
  ITask,
  ITaskCompetencies,
  ITaskCompetenciesRequest,
  ITaskCompetency,
  ITaskImportResponse,
  ITaskListResponse,
} from '../../interfaces/Task';
import { useLocale } from '../../providers/LocaleProvider';
import DownloadIcon from '../../resources/images/icons-download.svg';
import RemoveIcons from '../../resources/images/icons-remove.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import ImportTaskDetailDialog from './ImportTaskDetailDialog';
import ImportTaskListConfirmDialog from './ImportTaskListConfirmDialog';
import ImportTaskListDialog from './ImportTaskListDialog';
import ImportTasksErrorDialog from './ImportTasksErrorDialog';
import LimitedOverlay from './LimitedOverlay';
import { SetttingStandardsLibaryListingModal } from './SetttingStandardsLibaryListingModal';

const SettingStandardsLibraryTasks = ({ theme }: any) => {
  const { translate } = useLocale();
  const { tasks, setRouteState, debounceSearch, isTrial } = useStandardsLibrary();
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [showImportTaskDuplicatedDialog, setShowImportTaskDuplicatedDialog] = useState(false);
  const [showImportTaskDialog, setShowImportTaskDialog] = useState(false);
  const [showImportTaskDetail, setShowImportTaskDetailDialog] = useState(false);
  const [actionTarget, setActionTarget] = useState<null | HTMLElement>(null);
  const [currentRow, setCurrentRow] = useState<any>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showImportTaskListConfirmDialog, setShowImportTaskListConfirmDialog] = useState({
    isOpen: false,
    type: 'SINGLE',
  });
  const [showMessage, setShowMessage] = useState(false);
  const [showStandardsLibraryModalCompetencies, setShowStandardsLibraryModalCompetencies] =
    useState(false);
  const [showStandardsLibraryModalJobs, setShowStandardsLibraryModalJobs] = useState(false);
  const [cacheRows, setCacheRows] = useState<ITaskImportResponse>({
    importedTasks: [],
    notImportedTasks: [],
    failedToImportTasks: [],
  });
  const [competencies, setCompetencies] = useState<ITaskCompetency[]>([]);
  const [showNotImportedTasks, setShowNotImportedTasks] = useState(false);
  const [showImportTasksError, setShowImportTasksError] = useState(false);

  const ref = useRef<{ id: string; name: string }>({ id: '', name: '' });
  const pagingModel = tasks.pagingModel;
  const sortModel = tasks.sortModel;

  const { data: listTasksData, isLoading: isLoadingTask } = useAppQuery<ITaskListResponse>(
    `/StandardLibrary/tasks`,
    {
      config: {
        params: {
          pageNumber: pagingModel.page + 1,
          pageSize: pagingModel.pageSize,
          orderBy: sortModel.length > 0 ? `${sortModel[0].field} ${sortModel[0].sort}` : undefined,
          searchText: (tasks.queryText || '').trim(),
          showExtra: true,
        },
      },
    }
  );

  const taskListingCompetencies = useAppMutation<
    AxiosResponse<ITaskCompetencies>,
    unknown,
    ITaskCompetenciesRequest
  >('post', '/StandardLibrary/tasks/competencies', {
    onSuccess: (data) => {
      setShowImportTaskDuplicatedDialog(false);
      if (data.data.competencies.length > 0) {
        setShowImportTaskListConfirmDialog({ isOpen: true, type: 'LIST' });
        setCompetencies(data.data.competencies);
      } else {
        setShowImportTaskListConfirmDialog({ isOpen: false, type: 'LIST' });
        onImportTaskListConfirm();
      }
    },
  });

  const importTasksMutation = useAppMutation<AxiosResponse<ITaskImportResponse>, unknown, string[]>(
    'post',
    '/StandardLibrary/tasks/import',
    {
      onSuccess: (data) => {
        if (data.data.importedTasks.length > 0) {
          setShowMessage(true);
        }

        const errorTasks = concat(data.data.notImportedTasks, data.data.failedToImportTasks);

        if (errorTasks.length > 0) {
          setShowNotImportedTasks(true);
        }
        setCacheRows(data.data);
        setSelectedRows([]);
        setShowImportTaskDialog(false);
        setShowImportTaskDuplicatedDialog(false);
        setShowImportTaskDetailDialog(false);
        setShowImportTaskListConfirmDialog({ isOpen: false, type: 'SINGLE' });
      },
    }
  );

  const isLimitedAccess = useMemo(() => {
    if (isTrial && listTasksData?.standardLibraryTasks?.data) {
      const tasks = listTasksData?.standardLibraryTasks?.data;
      return tasks.some((t) => t.isExtra);
    }
    return false;
  }, [listTasksData, isTrial]);

  const rows = useMemo(() => {
    if (isLoadingTask || !listTasksData?.standardLibraryTasks?.data) {
      return [];
    }
    return listTasksData.standardLibraryTasks.data;
  }, [listTasksData, isLoadingTask]);

  const rowCount = listTasksData?.standardLibraryTasks?.totalCount || null;

  const extraTasks = useMemo(() => rows.filter((t) => t.isExtra), [rows]);

  const onClear = React.useCallback(() => {
    setSelectedRows([]);
  }, []);

  const setStateOfMessage = useCallback((state: boolean) => {
    setShowMessage(state);
  }, []);

  const onImportListTask = () => {
    setShowImportTaskDuplicatedDialog(true);
  };

  const onImportTaskDialog = () => {
    setActionTarget(null);
    setShowImportTaskDialog(true);
  };

  const onImportTask = (taskId: string) => {
    importTasksMutation.mutate([taskId]);
  };

  const onImportTaskSingle = (taskId: string) => {
    taskListingCompetencies.mutate(
      {
        taskIds: [taskId],
      },
      {
        onSuccess: (data) => {
          setShowImportTaskDetailDialog(false);
          setShowImportTaskDialog(false);

          if (data.data.competencies.length > 0) {
            setShowImportTaskListConfirmDialog({ isOpen: true, type: 'SINGLE' });
            setCompetencies(data.data.competencies);
          } else {
            onImportTask(taskId);
          }
        },
      }
    );
  };

  const onCloseImportTaskDuplicatedDialog = React.useCallback(() => {
    setShowImportTaskDuplicatedDialog(false);
  }, []);

  const onCloseImportTaskDialog = React.useCallback(() => {
    setShowImportTaskDialog(false);
  }, []);

  const onImportTaskList = () => {
    taskListingCompetencies.mutate({
      taskIds: selectedRows.map(String),
    });
  };

  const onCloseActionMenu = () => {
    setActionTarget(null);
  };

  const handleViewDetail = () => {
    setActionTarget(null);
    setShowImportTaskDetailDialog(true);
    setSelectedTaskId(String(currentRow?.id));
  };

  const handleActionMenu = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setActionTarget(event.currentTarget);
    setCurrentRow(row);
  };

  const isVisible = selectedRows.length > 0;

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setRouteState('tasks', {
      pagingModel:
        newModel.pageSize !== pagingModel.pageSize
          ? { page: 0, pageSize: newModel.pageSize }
          : newModel,
    });
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setRouteState('tasks', { sortModel: newSortModel });
    setRouteState('tasks', { pagingModel: { ...pagingModel, page: 0 } });
  };

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const value = event.target.value;
    debounceSearch('tasks', value);
  };

  const handleTaskSelect = (event: unknown, row: any) => {
    setSelectedTaskId(String(row.id));
    setShowImportTaskDetailDialog(true);
    setCurrentRow(row);
  };

  const onCloseImportTaskDetail = () => {
    setShowImportTaskDetailDialog(false);
  };

  const extractorCompetencies = useCallback((data: any) => {
    return data?.competencies || [];
  }, []);

  const extractorJobs = useCallback((data: any) => {
    return data?.jobs || [];
  }, []);

  const onClickChipCompetencyTasks = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>, row: any) => {
      event.stopPropagation();
      event.preventDefault();
      ref.current = {
        id: row.id,
        name: row.name,
      };
      setShowStandardsLibraryModalCompetencies(true);
    },
    []
  );

  const onClickChipCompetencyJob = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>, row: any) => {
      event.stopPropagation();
      event.preventDefault();
      ref.current = {
        id: row.id,
        name: row.name,
      };
      setShowStandardsLibraryModalJobs(true);
    },
    []
  );

  const onCloseCompetenciesListingModal = useCallback(() => {
    setShowStandardsLibraryModalCompetencies(false);
  }, []);

  const onCloseCompetenciesListingJobs = useCallback(() => {
    setShowStandardsLibraryModalJobs(false);
  }, []);

  const isRowSelectable = useCallback((data: any) => {
    return !data.row.isExtra;
  }, []);

  const onCloseImportTaskListConfirmDialog = useCallback(() => {
    setShowImportTaskListConfirmDialog({ isOpen: false, type: 'SINGLE' });
  }, []);

  const onImportTaskListConfirm = useCallback(() => {
    importTasksMutation.mutate(selectedRows.map(String));
  }, [selectedRows]);

  const onImportTasksError = useCallback(() => {
    setShowImportTasksError(true);
    setShowNotImportedTasks(false);
  }, []);
  const onCloseImportTasksError = useCallback(() => {
    setShowImportTasksError(false);
  }, []);

  const [element, setElement] = useState<Element | null>(null);

  useEffect(() => {
    // Query the element after the DataGrid has mounted
    const el = document.querySelector('div.MuiDataGrid-main');
    if (el) {
      setElement(el);
    }
  }, [isLoadingTask, rows]);

  const errorTasks = useMemo(
    () => concat(cacheRows.notImportedTasks, cacheRows.failedToImportTasks),
    [cacheRows.notImportedTasks, cacheRows.failedToImportTasks]
  );

  const hasCompetencies = useCallback((tasks: ITask[]) => {
    if (tasks.length > 1) {
      return tasks.some((task) => task.numberOfCompetencies);
    }
    return Boolean(tasks.find((task) => task.numberOfCompetencies));
  }, []);

  // Filer tasks
  const selectedRowsTask = useMemo(() => {
    return rows.filter((row) => selectedRows.includes(row.id));
  }, [rows, selectedRows]);

  const currentRowTask = useMemo(() => {
    return currentRow ? rows.filter((row) => row.id === currentRow.id) : [];
  }, [rows, currentRow]);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        spacing={4}
        sx={{
          '.MuiDataGrid-columnHeader:focus-within': {
            outline: 'none !important',
          },
          '.MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
          '.MuiDataGrid-columnHeader:focus-visible': {
            outline: 'none !important',
          },
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
          <TextField
            dense={true}
            placeholder={translate('placeholders.searchTask')}
            autoComplete={false}
            leadingIcon={<SearchIcon />}
            fullWidth={true}
            value={tasks.searchText || ''}
            onChange={handleSearchChange}
            sx={{ maxWidth: { md: '300px' }, width: '300px' }}
          />
        </Stack>
        <StyledStandardLibraryTable>
          <StandardLibraryTable<ITask>
            theme={theme}
            translate={translate}
            columns={[
              {
                type: (StandardLibraryTable as any).columns.name,
                label: translate('entities.task'),
              },
              {
                type: (StandardLibraryTable as any).columns.competencies,
                label: translate('tableColumns.competencies'),
                enableChip: true,
                onClick: onClickChipCompetencyTasks as any,
              },
              {
                type: (StandardLibraryTable as any).columns.jobs,
                label: translate('tableColumns.jobs'),
                enableChip: true,
                onClick: onClickChipCompetencyJob,
              },
              { type: (StandardLibraryTable as any).columns.actions },
            ]}
            paginationModel={pagingModel}
            isRowSelectable={isRowSelectable}
            onPaginationModelChange={handlePaginationModelChange}
            paginationMode='server'
            rows={isLoadingTask ? [] : rows}
            totalCount={rowCount || 0}
            isLoading={isLoadingTask}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            sortingMode='server'
            filterMode='server'
            dense={true}
            paper={false}
            checkboxSelection
            onRowSelectionModelChange={(rowSelectionModel: GridRowSelectionModel) => {
              setSelectedRows(rowSelectionModel);
            }}
            onJobSelect={handleTaskSelect}
            rowSelectionModel={selectedRows}
            selectable={false}
            onActionMenu={handleActionMenu}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer !important' } }}
            keyword={tasks.searchText}
          />
        </StyledStandardLibraryTable>
      </Stack>

      <Menu
        open={actionTarget !== null}
        anchorEl={actionTarget}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={onCloseActionMenu}
      >
        <MenuItem onClick={handleViewDetail}>
          <ListItemText>{translate('common.viewDetails')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={onImportTaskDialog}>
          <ListItemText>{translate('common.import')}</ListItemText>
        </MenuItem>
      </Menu>

      <Fade in={showImportTaskDetail} unmountOnExit>
        <div>
          <ImportTaskDetailDialog
            theme={theme}
            taskId={selectedTaskId}
            onClose={onCloseImportTaskDetail}
            open={showImportTaskDetail}
            onImport={(taskId: string) => {
              const tasks = rows.filter((row) => row.id === taskId);
              hasCompetencies(tasks) ? onImportTaskSingle(taskId) : onImportTask(taskId);
            }}
            isImporting={taskListingCompetencies.isPending || importTasksMutation.isPending}
          />
        </div>
      </Fade>

      <ImportTaskListDialog
        theme={theme}
        onClose={onCloseImportTaskDuplicatedDialog}
        open={showImportTaskDuplicatedDialog}
        tasks={selectedRowsTask}
        hasCompetencies={hasCompetencies(selectedRowsTask)}
        onImport={() => {
          hasCompetencies(selectedRowsTask) ? onImportTaskList() : onImportTaskListConfirm();
        }}
        isLoading={taskListingCompetencies.isPending || importTasksMutation.isPending}
      />

      <ImportTaskListDialog
        theme={theme}
        onClose={onCloseImportTaskDialog}
        open={showImportTaskDialog}
        tasks={currentRowTask}
        hasCompetencies={hasCompetencies(currentRowTask)}
        onImport={() => {
          hasCompetencies(currentRowTask)
            ? onImportTaskSingle(currentRow?.id)
            : onImportTask(currentRow?.id);
        }}
        isLoading={taskListingCompetencies.isPending || importTasksMutation.isPending}
      />

      <ImportTaskListConfirmDialog
        theme={theme}
        onClose={onCloseImportTaskListConfirmDialog}
        open={showImportTaskListConfirmDialog.isOpen}
        tasks={competencies}
        onImport={
          showImportTaskListConfirmDialog.type === 'SINGLE'
            ? () => onImportTask(currentRow?.id)
            : onImportTaskListConfirm
        }
        isLoading={importTasksMutation.isPending}
        isMultiTasks={selectedRows.length > 1}
      />

      <AnimatedFooter show={isVisible}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography>
            {selectedRows.length === 1
              ? translate('settingsViews.tasks.importTasks.taskSelected', {
                  count: selectedRows.length,
                })
              : translate('settingsViews.tasks.importTasks.tasksSelected', {
                  count: selectedRows.length,
                })}
          </Typography>
          <Stack direction='row' spacing={2}>
            <Button
              size='md'
              label={translate('common.clear')}
              onClick={onClear}
              sx={{ flexGrow: 1, md: { display: 'none' } }}
            />
            <Button
              startIcon={<DownloadIcon />}
              onClick={onImportListTask}
              label={translate('common.import')}
              variant='filled'
              size='md'
            />
          </Stack>
        </Box>
      </AnimatedFooter>

      <Fade timeout={300} in={showStandardsLibraryModalCompetencies} unmountOnExit>
        <div>
          <SetttingStandardsLibaryListingModal
            theme={theme}
            open={showStandardsLibraryModalCompetencies}
            columns={[
              {
                type: (StandardLibraryTable as any).columns.name,
                label: translate('tableColumns.competencies'),
              },
              {
                type: (StandardLibraryTable as any).columns.category,
                label: translate('tableColumns.category'),
              },
              {
                type: (StandardLibraryTable as any).columns.training,
                label: translate('tableColumns.training'),
              },
              {
                type: (StandardLibraryTable as any).columns.expiry,
                label: translate('tableColumns.expiry'),
              },
            ]}
            url={`/StandardLibrary/tasks/${ref.current.id}/competencies`}
            extractor={extractorCompetencies as any}
            onClose={onCloseCompetenciesListingModal}
            title={translate('tableColumns.competencies')}
            name={ref.current.name}
          />
        </div>
      </Fade>

      <Fade timeout={300} in={showStandardsLibraryModalJobs} unmountOnExit>
        <div>
          <SetttingStandardsLibaryListingModal
            theme={theme}
            open={showStandardsLibraryModalJobs}
            columns={[
              {
                type: (StandardLibraryTable as any).columns.name,
                label: translate('entities.job'),
              },
              {
                type: (StandardLibraryTable as any).columns.competencies,
                label: translate('tableColumns.competencies'),
              },
              {
                type: (StandardLibraryTable as any).columns.generalCount,
                field: 'numberOfTrainings',
                label: translate('entities.training'),
              },
              {
                type: (StandardLibraryTable as any).columns.tasks,
                label: translate('entities.tasks'),
              },
            ]}
            url={`/StandardLibrary/tasks/${ref.current.id}/jobs`}
            name={ref.current.name}
            extractor={extractorJobs as any}
            onClose={onCloseCompetenciesListingJobs}
            title={translate('entities.jobs')}
          />
        </div>
      </Fade>

      <Fade in={showMessage} timeout={300} unmountOnExit>
        <div>
          <Snackbar
            open={true}
            duration={3000}
            type={'success'}
            onClose={() => setStateOfMessage(false)}
            message={`${cacheRows.importedTasks.length === 1 ? translate('settingsViews.tasks.importTasks.taskImported', { count: cacheRows.importedTasks.length }) : translate('settingsViews.tasks.importTasks.tasksImported', { count: cacheRows.importedTasks.length })}. ${cacheRows.notImportedTasks.length === 1 ? translate('settingsViews.tasks.importTasks.taskAlreadyExisted', { count: cacheRows.notImportedTasks.length }) : translate('settingsViews.tasks.importTasks.tasksAlreadyExisted', { count: cacheRows.notImportedTasks.length })}.`}
          />
        </div>
      </Fade>

      <Fade in={showNotImportedTasks} timeout={300} unmountOnExit>
        <div>
          <Snackbar
            open={true}
            duration={3000}
            type={'error'}
            onClose={() => setShowNotImportedTasks(false)}
            message={
              <React.Fragment>
                <Typography hidden={errorTasks.length === 0}>
                  {errorTasks.length === 1
                    ? translate('settingsViews.tasks.importTasks.taskNotImported', {
                        count: errorTasks.length,
                      })
                    : translate('settingsViews.tasks.importTasks.tasksNotImported', {
                        count: errorTasks.length,
                      })}
                </Typography>
              </React.Fragment>
            }
            action={
              <React.Fragment>
                <div
                  style={{
                    marginLeft: 'auto',
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  hidden={errorTasks.length === 0}
                  className='klayo-snackbar_text'
                  onClick={onImportTasksError}
                >
                  {translate('common.viewDetails')}
                </div>
                <BlackIconContainer
                  style={{
                    padding: '0',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  onClick={() => {
                    setShowNotImportedTasks(false);
                  }}
                  className='klayo-snackbar_text '
                >
                  <RemoveIcons />
                </BlackIconContainer>
              </React.Fragment>
            }
          />
        </div>
      </Fade>

      <ImportTasksErrorDialog
        theme={theme}
        open={showImportTasksError}
        onClose={onCloseImportTasksError}
        tasks={errorTasks}
      />

      {isLimitedAccess && (
        <LimitedOverlay element={element} height={extraTasks.length * 56} translate={translate} />
      )}
    </React.Fragment>
  );
};

export default memo(SettingStandardsLibraryTasks);

const StyledStandardLibraryTable = styled.div`
  .MuiDataGrid-columnHeader:focus-within {
    outline: none !important;
  }
`;

const BlackIconContainer = styled.div`
  svg {
    path[fill='#1B3DF2'] {
      fill: #000;
    }
  }
`;
